import cx from 'classnames';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import { Label } from '@trucktrax/trucktrax-common';
import { getRequest } from '../../../services/requestService';
import { getSecurityBaseUrl } from '../../../util/apiUtil';
import { normalizeEmail } from '../../../util/emailNotificationUtil';
import { USERS_EMAIL_AUTOCOMPLETE_PATH } from '../../../constants/apiConstants';
import styles from './EmailRecipientField.module.css';

/**
 * CUSTOM_COMPONENT-compatible email recipient field (DEV-4155 / DEV-4170).
 *
 * DynamicForms renders CUSTOM_COMPONENT fields by spreading the field config
 * (which includes `key`) plus `value` and `onChange` onto the component. To
 * match the CardInput contract, changes propagate via `onChange({ [key]: value })`.
 *
 * Suggests existing users' emails from what the admin has typed so far (the
 * epic's partial-match requirement) and still accepts a free-typed address that
 * belongs to no user (Autocomplete freeSolo). Suggestions are best-effort: an
 * empty or failed lookup degrades to plain manual entry rather than blocking.
 *
 * Each suggestion is shown as `address (Owner Name)` so an admin choosing
 * between similar addresses can tell whose is whose. Only the address is ever
 * written into the field.
 */
export const MIN_SEARCH_LENGTH = 2;
export const SEARCH_DEBOUNCE_MS = 300;

export const SEARCH_PLACEHOLDER = 'Search users by name or email...';

export interface EmailRecipientFieldProps {
  id?: string;
  value?: string;
  /**
   * Reports the user the current address belongs to, or undefined when it belongs to
   * none. Fires when a suggestion is picked and when a whole known address is typed,
   * since both mean the same thing.
   *
   * Separate from `onChange` because the field's value is the address and nothing else:
   * `checkEmailAddress` validates a string and the submit sends a string, so the user
   * behind it travels alongside rather than inside it.
   */
  onUserChange?: (user?: EmailSuggestion) => void;
  label?: string;
  isRequired?: boolean;
  disabled?: boolean;
  dataTest?: string;
  errorMessage?: string;
  className?: string;
  onChange?: (event: { [key: string]: string }) => void;
}

/**
 * A suggested recipient: the address, the display name of the user it belongs to, and
 * that user's url so a caller can look the record up. `UserSlimDto` is
 * `(DisplayName, Email, UserUrl)` and carries nothing else -- notably no regions, which
 * is why narrowing by region takes a second read.
 */
export interface EmailSuggestion {
  email: string;
  name?: string;
  userUrl?: string;
}

/**
 * The security backend serializes UserSlimDto without JsonProperty attributes
 * and registers no camelCase resolver, so these arrive PascalCase. Both spellings
 * are read so a later backend casing fix cannot silently empty the dropdown.
 */
function toEmail(user: any): string | undefined {
  return user?.Email ?? user?.email;
}

/** Same PascalCase-first reasoning as {@link toEmail}. */
function toDisplayName(user: any): string | undefined {
  const name = user?.DisplayName ?? user?.displayName;
  return typeof name === 'string' && name.trim() !== '' ? name.trim() : undefined;
}

/**
 * The user's url. `UserSlimDto` names this field `UserUrl`, not `Url` -- reading `Url`
 * yields undefined and silently disables everything that depends on knowing which user
 * an address belongs to. Same PascalCase-first reasoning as {@link toEmail}.
 */
function toUserUrl(user: any): string | undefined {
  const url = user?.UserUrl ?? user?.userUrl;
  return typeof url === 'string' && url.trim() !== '' ? url.trim() : undefined;
}

/**
 * How a suggestion reads in the dropdown. Falls back to the bare address rather
 * than rendering an empty pair of parentheses for a user with no display name.
 */
export const suggestionLabel = (suggestion: EmailSuggestion) =>
  suggestion.name ? `${suggestion.email} (${suggestion.name})` : suggestion.email;

export default function EmailRecipientField(props: EmailRecipientFieldProps) {
  const {
    id,
    value = '',
    label,
    isRequired = false,
    disabled = false,
    dataTest,
    errorMessage,
    className,
    onChange,
    onUserChange,
  } = props;

  const [emailOptions, setEmailOptions] = useState<EmailSuggestion[]>([]);

  /**
   * What the input shows, which is not the field's value.
   *
   * The value is the address -- that is what a subscription stores and what the submit
   * sends -- but an admin picked a *person*, so the box says their name. Held locally
   * because the two diverge: MUI writes `getOptionLabel` into the input on selection
   * while `onChange` reports the address upwards.
   *
   * Undefined until something sets it, so the address can stand in until a name is
   * known -- see `display` below.
   */
  const [picked, setPicked] = useState<string | undefined>(undefined);

  const term = value.trim();

  useEffect(() => {
    // Below the threshold there is nothing worth narrowing on, and querying
    // would pull an arbitrary page of unrelated users.
    if (term.length < MIN_SEARCH_LENGTH) {
      setEmailOptions([]);
      return undefined;
    }

    let cancelled = false;

    const timer = setTimeout(async () => {
      try {
        /*
         * `searchTerm`, not `partialEmail`. The field picks a *user*, so typing a
         * name has to match as readily as typing an address -- `FilteredBySearchTerm`
         * is an OR across username, first name, last name, email, phone and employee
         * number, where `partialName` and `partialEmail` are separate filters chained
         * as AND and so cannot express "either".
         *
         * No `size`: the endpoint appends its own (defaulting to 20) on top of whatever
         * the query string already carries, so sending one produces `size=20&size=20`.
         * `BaseFilters.Size` is an int, that binds as "20,20", int conversion fails, and
         * an [ApiController] turns invalid model state into a 400 -- which this catches
         * and reports as no suggestions at all.
         */
        const response = await getRequest(getSecurityBaseUrl() + USERS_EMAIL_AUTOCOMPLETE_PATH, {
          searchTerm: term,
        });
        const items = response?.data?.items ?? [];
        // Deduped by address: two records for one address would render duplicate
        // rows, and React would warn on the repeated key.
        const seen = new Set<string>();
        const suggestions: EmailSuggestion[] = [];
        items.forEach((user: any) => {
          const email = toEmail(user);
          if (typeof email !== 'string' || email === '' || seen.has(email)) {
            return;
          }
          seen.add(email);
          suggestions.push({ email, name: toDisplayName(user), userUrl: toUserUrl(user) });
        });
        if (!cancelled) {
          setEmailOptions(suggestions);
        }
      } catch {
        if (!cancelled) {
          setEmailOptions([]);
        }
      }
    }, SEARCH_DEBOUNCE_MS);

    // Guards both the pending timer and an in-flight response, so a slow
    // reply for an earlier term cannot overwrite results for a later one.
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [term]);

  /**
   * The user the address currently names, if the suggestions include it. Matched on the
   * normalized address rather than on the pick event, so typing a known address whole --
   * or pasting one -- resolves the same user as choosing it from the list.
   */
  const matchedUser = useMemo(
    () => emailOptions.find(option => normalizeEmail(option.email) === normalizeEmail(value)),
    [emailOptions, value],
  );

  /*
   * Reported only when it actually changes. `onUserChange` is rebuilt by the caller on
   * every render, so an effect keyed on it alone would fire in a loop; the ref makes the
   * resolved user the thing that matters.
   *
   * Keyed on the address and the url together, not the url alone. A user the backend
   * returned without one gives `undefined`, which is also what no match gives -- so a
   * url-only key read "matched a user with no url" as "matched nobody" and stayed
   * silent.
   */
  // Starts at the no-match identity, so a field that has never matched a user stays
  // silent rather than opening with a redundant "no user" report.
  const reportedUser = useRef<string>('');
  useEffect(() => {
    const identity = matchedUser
      ? `${normalizeEmail(matchedUser.email)}|${matchedUser.userUrl ?? ''}`
      : '';
    if (reportedUser.current !== identity) {
      reportedUser.current = identity;
      onUserChange?.(matchedUser);
    }
  }, [matchedUser, onUserChange]);

  const handleChange = (newValue: string) => {
    if (onChange && id) {
      onChange({ [id]: newValue });
    }
  };

  /**
   * The name where one is known, the address otherwise.
   *
   * `picked` covers a fresh selection. `matchedUser` covers arriving with a value
   * already set -- editing an existing entry, where the address is all the screen has
   * and the name has to be resolved before it can be shown. Falls back to the raw
   * value so the box is never blank while that resolves, and so a typed address that
   * belongs to nobody shows exactly what was typed.
   */
  const display = picked ?? matchedUser?.name ?? value;

  return (
    <div className={cx(styles.field, className)}>
      {/*
        Only when there is one. An empty Label still renders an element with margin, which
        pushed the input below anything sitting beside it in a row.
      */}
      {label ? <Label isRequired={isRequired}>{label}</Label> : null}
      <Autocomplete<EmailSuggestion, false, false, true>
        freeSolo
        /*
         * No popup indicator, which is what freeSolo gives by default. One was forced
         * back briefly on the grounds that the field otherwise looked like a plain text
         * box -- but it reads as a select's arrow while opening nothing, since there are
         * no options until two characters are typed. The placeholder says what to do
         * without promising a list that is not there.
         */
        disabled={disabled}
        options={emailOptions}
        inputValue={display}
        // The API has already narrowed by the typed term; re-filtering client
        // side would drop matches the server found on other fields.
        filterOptions={options => options}
        /*
         * What MUI writes into the input on selection: the person's name, since that is
         * what was chosen. The address is still the field's value and still what the
         * dropdown lists -- see renderOption and the epic's wording.
         */
        getOptionLabel={option =>
          typeof option === 'string' ? option : (option.name ?? option.email)
        }
        /*
         * Selection reports the address upward while the input shows the name. Typing
         * goes through onInputChange instead, where what was typed *is* the address.
         */
        onChange={(_event, option) => {
          if (option && typeof option !== 'string') {
            setPicked(option.name ?? option.email);
            handleChange(option.email);
          }
        }}
        renderOption={(optionProps, option) => (
          <li {...optionProps} key={option.email}>
            {suggestionLabel(option)}
          </li>
        )}
        onInputChange={(_event, newValue, reason) => {
          // Only a keystroke is an address being typed. MUI also fires this with the
          // option label on selection ('reset'), which would otherwise store the name
          // as the address.
          if (reason === 'input') {
            setPicked(undefined);
            handleChange(newValue);
          }
        }}
        renderInput={renderParams => (
          <TextField
            {...renderParams}
            variant="standard"
            margin="none"
            fullWidth
            placeholder={SEARCH_PLACEHOLDER}
            error={!!errorMessage}
            helperText={errorMessage}
            // The app's inputs are tt-input boxes; a bare MUI standard TextField renders
            // as an underline and read as unstyled next to the other fields in the modal.
            // InputProps must be spread, not replaced: Autocomplete puts its ref and
            // endAdornment there.
            InputProps={{ ...renderParams.InputProps, disableUnderline: true }}
            inputProps={{
              ...renderParams.inputProps,
              // tt-input supplies the box, but MUI's Autocomplete rules beat its
              // padding -- `.field` in this component's module restates it. Both are
              // needed; neither is sufficient.
              className: cx(renderParams.inputProps?.className, 'tt-input'),
              'data-test': dataTest,
            }}
          />
        )}
      />
    </div>
  );
}
